"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { ArrowUp, Check } from "lucide-react"

/**
 * The hero: "How's my training going?" answered in the chat, as a live
 * frame rather than a picture. The plan card is the card's own code
 * (public/play/plan-card.html, copied from garmin-mcp) drawing the example
 * marathon block four weeks in, cropped with a fade so the headline stays
 * beside it; the reply under it says what the card can't. Light throughout,
 * like the "Your plan, in the chat" section further down.
 *
 * It plays once, when the frame is well into view: the question, the tool
 * line working then done, the card fading up with this week pulsed, the
 * reply streaming in word by word. Everything is laid out from the start
 * and only shown, so nothing below moves while it plays. Someone who asks
 * for less motion sees the finished state at once.
 */

/** The example block's session days (Mon = 0): Tue, Wed, Thu, Fri, Sun. */
const SESSION_DAYS = [1, 2, 3, 4, 6]
/** Sessions planned in the four finished weeks, and the one missed among them. */
const PAST_PLANNED = 20
const PAST_MISSED = 1

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)"
/** The sequence, in milliseconds from the start. */
const AT = { pill: 0, tool: 600, tick: 1400, card: 1600, pulse: 1900, reply: 2400 }
/** Words a second, as Claude streams. */
const WORDS_PER_SECOND = 20

/** 0 nothing, 1 pill, 2 tool line working, 3 tool line done, 4 card, 5 reply streaming, 6 rested. */
type Phase = 0 | 1 | 2 | 3 | 4 | 5 | 6

export function HeroChat() {
  const [height, setHeight] = useState(760)
  const [ready, setReady] = useState(false)
  const [phase, setPhase] = useState<Phase>(0)
  const [shownWords, setShownWords] = useState(0)
  const [still, setStill] = useState(false)
  const frame = useRef<HTMLIFrameElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const timers = useRef<number[]>([])
  const started = useRef(false)

  // The reply's figures follow the same rule the example does: four weeks
  // done with one session missed, and this week's sessions before today done.
  const today = (new Date().getDay() + 6) % 7
  const thisWeek = SESSION_DAYS.filter((d) => d < today).length
  const done = PAST_PLANNED - PAST_MISSED + thisWeek
  const planned = PAST_PLANNED + thisWeek
  const reply = [
    `Week 5 of 12, and you’ve done ${done} of ${planned} sessions so far.`,
    "The key one this week is Wednesday’s threshold: 6 × 1 km at 3:55–4:05/km, with 200 m jogs between.",
    "Want me to walk you through it?",
  ].join(" ")
  const words = reply.split(" ")

  const clear = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }
  const after = (ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms))
  }

  // This week, pulsed once on the card itself: its current-week bar, and the
  // next session's row for a frame tall enough to show it. The card is the
  // same origin, so a little style goes in with the class.
  const pulseCard = useCallback(() => {
    try {
      const doc = frame.current?.contentDocument
      if (!doc) return
      if (!doc.getElementById("hero-pulse")) {
        const style = doc.createElement("style")
        style.id = "hero-pulse"
        style.textContent =
          `@keyframes heroPulse{0%{box-shadow:0 0 0 0 rgba(224,49,42,0)}35%{box-shadow:0 0 0 6px rgba(224,49,42,.28)}100%{box-shadow:0 0 0 0 rgba(224,49,42,0)}}` +
          `.hero-pulse{animation:heroPulse 900ms ${EASE} 1;border-radius:inherit}`
        doc.head.appendChild(style)
      }
      doc.querySelectorAll(".bar[aria-pressed='true'] .bar-box, .next").forEach((node) => {
        node.classList.remove("hero-pulse")
        void (node as HTMLElement).offsetWidth
        node.classList.add("hero-pulse")
      })
    } catch {
      /* another origin: no pulse */
    }
  }, [])

  const play = useCallback(() => {
    clear()
    setPhase(1)
    setShownWords(0)
    after(AT.tool, () => setPhase(2))
    after(AT.tick, () => setPhase(3))
    after(AT.card, () => setPhase(4))
    after(AT.pulse, pulseCard)
    after(AT.reply, () => {
      setPhase(5)
      const step = 1000 / WORDS_PER_SECOND
      for (let i = 1; i <= words.length; i++) {
        after(i * step, () => setShownWords(i))
      }
      after(words.length * step + 200, () => setPhase(6))
    })
  }, [pulseCard, words.length])

  // Less motion asked for: the finished frame, straight away.
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setStill(true)
      setPhase(6)
      setShownWords(words.length)
      started.current = true
    }
  }, [words.length])

  // Once, when at least 40% of the frame is in view.
  useEffect(() => {
    const node = box.current
    if (!node || started.current) return
    const seen = new IntersectionObserver(
      (entries) => {
        if (started.current || !entries.some((e) => e.isIntersecting)) return
        started.current = true
        seen.disconnect()
        play()
      },
      { threshold: 0.4 },
    )
    seen.observe(node)
    return () => seen.disconnect()
  }, [play])

  useEffect(() => clear, [])

  // The frame sits at the top of the page, so it can finish loading before
  // React has attached anything to it: its load event and first height
  // report are then missed. Read the card's own height until it has drawn.
  useEffect(() => {
    const read = () => {
      try {
        const doc = frame.current?.contentDocument
        const h = doc?.documentElement?.scrollHeight
        if (doc?.readyState === "complete" && h && h > 100 && doc.querySelector(".row")) {
          setHeight(Math.ceil(h))
          setReady(true)
          return true
        }
      } catch {
        /* another origin: the messages below take over */
      }
      return false
    }
    if (read()) return
    const timer = window.setInterval(() => {
      if (read()) window.clearInterval(timer)
    }, 250)
    const stop = window.setTimeout(() => window.clearInterval(timer), 8000)
    return () => {
      window.clearInterval(timer)
      window.clearTimeout(stop)
    }
  }, [])

  // From then on the card reports its height itself (a tapped session opens),
  // and answers nothing else here: no buttons need the chat in the hero.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return
      const d = event.data
      if (!d || d.jsonrpc !== "2.0") return
      if (d.method === "ui/notifications/size-changed" && d.params?.height) {
        setHeight(Math.ceil(d.params.height))
        setReady(true)
      } else if (d.method === "tools/call") {
        // A tapped session asks for its details: the example answers, as it
        // does in the section below.
        const name = typeof d.params?.name === "string" ? d.params.name : "get_plan"
        frame.current?.contentWindow?.postMessage(
          { type: "daash-play", action: "call", id: d.id, name, arguments: d.params?.arguments ?? {} },
          window.location.origin,
        )
      } else if (d.id != null && d.method) {
        frame.current?.contentWindow?.postMessage({ jsonrpc: "2.0", id: d.id, result: {} }, window.location.origin)
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [])

  // Each part is in place from the start and only revealed, so the frame
  // keeps its full height while it plays.
  const reveal = (on: boolean, ms = 400, rise = true): React.CSSProperties => ({
    opacity: on ? 1 : 0,
    transform: on || !rise ? "none" : "translateY(8px)",
    transition: still ? "none" : `opacity ${ms}ms ${EASE}, transform ${ms}ms ${EASE}`,
  })

  return (
    <div className="flex w-full flex-col gap-2">
      <div
        ref={box}
        className="flex w-full flex-col gap-3 rounded-[20px] border border-[#E6E8EC] bg-white p-4 shadow-[0_12px_40px_-24px_rgba(22,27,38,0.35)]"
        aria-live="off"
      >
        <p
          className="m-0 max-w-[88%] self-end rounded-[14px] bg-[#161B26] px-3.5 py-2.5 text-[14.5px] leading-[1.4] text-white"
          style={reveal(phase >= 1)}
        >
          How&rsquo;s my training going?
        </p>
        <div className="flex items-center gap-2 text-[13px] text-[#5B6270]" style={reveal(phase >= 2, 300, false)}>
          <span aria-hidden className="grid size-[18px] place-items-center rounded-[5px] bg-[#E0312A] text-[11px] font-bold italic leading-none text-white">
            D
          </span>
          <span className="font-semibold text-[#161B26]">Daash</span>
          <span className="font-mono text-[12.5px]">get_plan</span>
          {phase >= 3 ? (
            <Check className="size-3.5 text-[#3F8A47]" aria-hidden />
          ) : (
            <span aria-hidden className="size-3.5 animate-spin rounded-full border-2 border-[#E6E8EC] border-t-[#E0312A]" />
          )}
        </div>
        <div
          className="relative h-[400px] overflow-hidden rounded-[16px] border border-[#E6E8EC] bg-white"
          style={reveal(phase >= 4 && ready, 300)}
        >
          {!ready && <div aria-hidden className="m-shimmer absolute inset-0" />}
          <iframe
            ref={frame}
            src="/play/plan-card.html?mode=hero"
            title="A 12-week marathon block four weeks in, drawn by the Daash plan card: the block's shape, this week's sessions done, and the next one."
            onLoad={() => window.setTimeout(() => setReady(true), 400)}
            style={{ height }}
            className="relative block w-full border-0 bg-white"
          />
          <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-white/0 to-white" />
        </div>
        <p className="m-0 text-[16px] leading-[1.5] text-[#161B26]" aria-label={reply}>
          {words.map((word, i) => (
            <span
              key={i}
              aria-hidden
              style={{ opacity: i < shownWords ? 1 : 0, transition: still ? "none" : `opacity 120ms ${EASE}` }}
            >
              {word}
              {i < words.length - 1 ? " " : ""}
            </span>
          ))}
        </p>
        <div aria-hidden className="flex items-center gap-3 rounded-full border border-[#E6E8EC] py-2 pl-4 pr-2 text-[14.5px] text-[#9AA1AD]">
          <span className="flex-1">Ask anything</span>
          <span className="grid size-8 place-items-center rounded-full bg-[#E6E8EC] text-white">
            <ArrowUp className="size-4" />
          </span>
        </div>
      </div>
      <div className="flex h-5 justify-end">
        {phase === 6 && !still && (
          <button
            type="button"
            onClick={play}
            className="text-[13px] text-[#5B6270] underline-offset-[3px] hover:underline"
            style={reveal(true, 300, false)}
          >
            Replay
          </button>
        )}
      </div>
    </div>
  )
}
